import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
    FaBell, FaBellSlash, FaTimes, FaBullhorn, FaClipboardList, FaCommentDots,
    FaExternalLinkAlt, FaArrowRight,
} from "react-icons/fa";
import { apiFetch } from "../utils/api";
import { timeAgo } from "../utils/time";

/** How often the bell re-checks for new notifications while the tab is visible. */
const POLL_MS = 60000;

/**
 * The notification bell — an EMPLOYEE-ONLY feature.
 *
 * <Header/> only MOUNTS this when the viewer is authenticated. That is deliberate and
 * load-bearing: this component fetches employee notification data on render, so
 * merely hiding it with CSS would still pull that data into a visitor's browser.
 * See Header.test.js, which asserts a visitor never issues the request.
 */
const NotificationBell = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const [manual, setManual] = useState(null);

    const wrapRef = useRef(null);
    const buttonRef = useRef(null);

    /**
     * Pull the current unread list from the server. Extracted so the bell can refresh
     * itself WITHOUT a page reload — a notification created (or scheduled to appear) after
     * the page loaded should surface on its own.
     *
     * Safe to call repeatedly: the server only ever returns still-unread, in-window items,
     * so a refetch can't resurrect one the reader already dismissed.
     */
    const loadNotifications = useCallback(async () => {
        try {
            const res = await apiFetch("/api/employee/notifications/list");
            const data = await res.json();
            if (data.success) setNotifications(data.data);
        } catch (err) {
            console.error("Failed to load notifications", err);
        }
    }, []);

    /**
     * Keep the bell live: an initial load, then a light background poll so the count
     * updates on its own. We also refetch the moment the reader returns to the tab or
     * opens the bell, so those feel instant rather than waiting on the next poll tick.
     * Polling pauses while the tab is hidden — no point hitting the server for a bell
     * nobody is looking at.
     */
    useEffect(() => {
        loadNotifications();

        let timer = null;
        const start = () => { if (!timer) timer = setInterval(loadNotifications, POLL_MS); };
        const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

        const onVisibility = () => {
            if (document.hidden) { stop(); }
            else { loadNotifications(); start(); }   // catch up immediately, then resume
        };

        if (!document.hidden) start();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            stop();
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [loadNotifications]);

    // Opening the bell always shows the freshest list, not a stale snapshot.
    useEffect(() => {
        if (open) loadNotifications();
    }, [open, loadNotifications]);

    // Close on outside click and on Escape — a dropdown you cannot dismiss with the
    // keyboard is a trap.
    useEffect(() => {
        if (!open && !manual) return;

        const onClick = (e) => {
            if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if (manual) setManual(null);
            else if (open) {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };

        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, manual]);

    const markRead = async (id) => {
        try {
            const res = await apiFetch("/api/employee/notifications/mark-read", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ notification_ids: [id] }),
            });
            const data = await res.json();
            if (data.success) {
                setNotifications((prev) => prev.filter((n) => n.notification_id !== id));
            }
        } catch (err) {
            console.error("Failed to mark as read", err);
        }
    };

    /**
     * A notification that POINTS AT CONTENT navigates straight to it, built from ref_id —
     * never guessed from the body.
     *
     *   UPDATE → the update detail route, id = ref_id
     *   POLICY → the policy detail route, id = ref_id
     *
     * link_url is deliberately NOT a target here. It used to be, which meant a MANUAL
     * notification with a link threw the reader out to another page before they could
     * read the message. It is now an action offered INSIDE the modal instead.
     *
     * NOTE: navigating here is not an authorisation decision. The target row has its
     * OWN audience, which the detail endpoint re-checks server-side — so a click-through
     * to something outside the viewer's audience correctly lands on a 404 page. Being
     * sent a notification does not grant access to the thing it points at.
     */
    const targetOf = (n) => {
        if (n.notification_type === "UPDATE" && n.ref_id) return `/update/${n.ref_id}`;
        if (n.notification_type === "POLICY" && n.ref_id) return `/policy/${n.ref_id}`;
        return null;
    };

    /** An absolute http(s) link leaves the app; anything else is an in-app route. */
    const isExternal = (url) => /^https?:\/\//i.test(String(url || ""));

    /**
     * Every notification that isn't a pointer to content opens the modal — including one
     * carrying a link_url, so the full message is always readable before the reader
     * chooses to follow the link.
     */
    const openNotification = (n) => {
        setOpen(false);

        const to = targetOf(n);
        if (to) return navigate(to);

        setManual(n);
    };

    const followLink = (url) => {
        setManual(null);
        navigate(url);
    };

    const iconFor = (n) => {
        if (n.icon) return <span aria-hidden="true">{n.icon}</span>;
        return {
            UPDATE: <FaBullhorn aria-hidden="true" />,
            POLICY: <FaClipboardList aria-hidden="true" />,
        }[n.notification_type] || <FaCommentDots aria-hidden="true" />;
    };

    const author = (n) => {
        if (!n?.created_by_first_name) return "System";
        return `${n.created_by_first_name} ${n.created_by_last_name || ""}`.trim();
    };

    /**
     * notification_value is prose for EVERY type now (the update's subject, the policy's
     * excerpt, or a manual message) — dAdmin no longer overloads it with a row id, so it
     * is printed as the body again. It can still be NULL when the author left the excerpt
     * blank, in which case the item renders as title-only rather than an empty line.
     */
    const bodyOf = (n) => {
        const text = n.notification_value?.trim();
        return text ? text : null;
    };

    /**
     * High-priority items are sorted to the top of the list by the server; this badge is
     * what explains WHY they sit above newer ones. Anything that isn't 'high' is normal —
     * we match the server's rule rather than enumerating the other values, so a new
     * priority string can never be mistaken for high.
     */
    const isHigh = (n) => String(n?.priority || "").toLowerCase() === "high";

    const unread = notifications.length;

    return (
        <>
            <div className="bell-wrap" ref={wrapRef}>
                <button
                    ref={buttonRef}
                    type="button"
                    className="bell-btn"
                    onClick={() => setOpen((o) => !o)}
                    aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
                    aria-expanded={open}
                    aria-haspopup="true"
                >
                    <FaBell aria-hidden="true" />
                    {unread > 0 && <span className="bell-count">{unread > 9 ? "9+" : unread}</span>}
                </button>

                {open && (
                    <div className="bell-panel">
                        <div className="bell-head">
                            <h2>Notifications</h2>
                            {unread > 0 && <span className="chip">{unread} new</span>}
                        </div>

                        {unread === 0 ? (
                            <div className="bell-empty">
                                <span className="empty-icon" aria-hidden="true"><FaBellSlash /></span>
                                <p>You're all caught up.</p>
                            </div>
                        ) : (
                            <ul className="bell-list">
                                {notifications.map((n) => (
                                    <li key={n.notification_id} className="bell-item">
                                        <button
                                            type="button"
                                            className="bell-item-main"
                                            onClick={() => openNotification(n)}
                                        >
                                            <span className="bell-icon">{iconFor(n)}</span>
                                            <span>
                                                <span className="bell-title">{n.title}</span>
                                                {bodyOf(n) && <span className="bell-msg">{bodyOf(n)}</span>}
                                                <span className="bell-meta">
                                                    {isHigh(n) && (
                                                        <span className="bell-priority" title="Priority">Priority</span>
                                                    )}
                                                    <time className="bell-time" dateTime={n.created_time}>
                                                        {timeAgo(n.created_time)}
                                                    </time>
                                                </span>
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            className="bell-dismiss"
                                            onClick={() => markRead(n.notification_id)}
                                            aria-label={`Mark "${n.title}" as read`}
                                        >
                                            <FaTimes aria-hidden="true" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Portaled to <body>: rendered in place it would be trapped by the header's
                backdrop-filter (which makes the header a containing block for position:
                fixed), so the modal centred on the 68px header and its top was cut off
                above the screen. In the body it centres on the viewport. */}
            {manual && createPortal(
                <div
                    className="notif-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="manual-title"
                    onMouseDown={(e) => { if (e.target === e.currentTarget) setManual(null); }}
                >
                    <div className="notif-modal">
                        <div className="notif-modal-head">
                            <span className="notif-modal-icon" aria-hidden="true">{iconFor(manual)}</span>
                            <h2 id="manual-title">{manual.title}</h2>
                            <button
                                type="button"
                                className="notif-modal-close"
                                onClick={() => setManual(null)}
                                aria-label="Close"
                            >
                                <FaTimes aria-hidden="true" />
                            </button>
                        </div>

                        {bodyOf(manual) && <div className="notif-modal-body">{bodyOf(manual)}</div>}

                        <div className="notif-modal-foot">
                            <span className="notif-modal-avatar" aria-hidden="true">
                                {author(manual).charAt(0).toUpperCase()}
                            </span>
                            <span className="notif-modal-who">
                                <strong>{author(manual)}</strong>
                                <time dateTime={manual.created_time}>{timeAgo(manual.created_time)}</time>
                            </span>

                            {/* The link is an action the reader takes AFTER reading, not
                                something that pre-empts the message. Absent link_url, the
                                footer is exactly what it was. */}
                            {manual.link_url && (isExternal(manual.link_url) ? (
                                <a
                                    className="notif-modal-action"
                                    href={manual.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open link <FaExternalLinkAlt aria-hidden="true" />
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className="notif-modal-action"
                                    onClick={() => followLink(manual.link_url)}
                                >
                                    Open link <FaArrowRight aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default NotificationBell;
